import { Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { PostalAddress } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';

import { countryName } from '@/features/markets';

export interface OrderAddressCardProps {
  title: ReactNode;
  address: PostalAddress | null;
  /** Rendered when staff may change the address (shipping while unfulfilled). */
  onEdit?: (() => void) | undefined;
  /** Shown when the address exists but is locked. */
  lockedHint?: string | undefined;
}

function formatAddressLines(address: PostalAddress): string[] {
  const name = [address.firstName, address.lastName].filter(Boolean).join(' ');
  const cityLine = [address.city, address.region, address.postalCode].filter(Boolean).join(', ');
  return [
    name,
    address.company ?? '',
    address.line1,
    address.line2 ?? '',
    cityLine,
    countryName(address.countryCode),
    address.phone ?? '',
  ].filter(Boolean);
}

export function OrderAddressCard({ title, address, onEdit, lockedHint }: OrderAddressCardProps) {
  const { t } = useTranslation('orders');
  return (
    <Card className="gap-3">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>{title}</CardTitle>
        {onEdit ? (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil aria-hidden />
            {t('actions.editAddress')}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {address ? (
          <address className="text-sm not-italic">
            {formatAddressLines(address).map((line, i) => (
              <span key={i} className="block">
                {line}
              </span>
            ))}
          </address>
        ) : (
          <p className="text-muted-foreground text-sm">{t('address.none')}</p>
        )}
        {lockedHint ? <p className="text-muted-foreground mt-2 text-xs">{lockedHint}</p> : null}
      </CardContent>
    </Card>
  );
}
